import { compare } from 'compare-versions';
import React, { memo, useState } from 'react';

import { IRoomWithDevicesAndFwInfo } from '../../interfaces/IOdooRoomsResponse';
import classes from './CustomerAppTemplate.module.css';

const MIN_FW_VERSION_DISABLE_NOTIFIED_STATE = '1.2.3';
export enum RoomCalibrationState {
    TEMP_STATE_CALIBRATED = 0, // We don't send anything in nominal mode
    TEMP_STATE_ONGOING_EXTERN = 1, // Someone triggered a calibration from outside
    TEMP_STATE_ONGOING = 2, // A calibration is ongoing
    TEMP_STATE_READY = 3, // Time conditions met, waiting for temperature conditions
    TEMP_STATE_FAST_DONE = 4, // Device regulating with fast parameters
    TEMP_STATE_FAST_ONGOING = 5, // A fast calibration is ongoing
    TEMP_STATE_FAST_READY = 6, // Time conditions met, waiting for temperature conditions
    TEMP_STATE_NOTIFIED = 7, // Device installed, waiting for enabling conditions (time/temp)
    TEMP_STATE_UNCALIBRATED = 8, // Device cannot regulate
}

export const checkRoomIsCalibrating = (
    roomData: IRoomWithDevicesAndFwInfo
): boolean => {
    return roomData.devices.some(
        (deviceData) =>
            deviceData.calibrationState ===
                RoomCalibrationState.TEMP_STATE_ONGOING ||
            deviceData.calibrationState ===
                RoomCalibrationState.TEMP_STATE_FAST_ONGOING ||
            deviceData.calibrationState ===
                RoomCalibrationState.TEMP_STATE_ONGOING_EXTERN
    );
};

export const checkRoomInNotifiedState = (
    roomData: IRoomWithDevicesAndFwInfo
): boolean => {
    const isAnyFwVersionOld = roomData.devices.some((device) =>
        compare(
            device.firmwareVersion,
            MIN_FW_VERSION_DISABLE_NOTIFIED_STATE,
            '<'
        )
    );

    if (!isAnyFwVersionOld) {
        return false;
    }
    return roomData.devices.some(
        (deviceData) =>
            deviceData.calibrationState ===
            RoomCalibrationState.TEMP_STATE_NOTIFIED
    );
};

interface CustomerAppTemplateProps {
    generateCustomerAppScreenshot: () => void;
    rooms: IRoomWithDevicesAndFwInfo[] | null;
    homeName: string | null;
}

// To Debug this component navigate to /after-close and remove style={{ position: 'absolute', opacity: 0 }} from TabAfterClose.tsx
const CustomerAppTemplate: React.FC<CustomerAppTemplateProps> = memo(
    ({ generateCustomerAppScreenshot, rooms, homeName }) => {
        const [loadedImages, setLoadedImages] = useState(0);
        const [imageGenerated, setImageGenerated] = useState(false);
        const isAnyRoomCalibrating =
            rooms?.some(
                (room) =>
                    checkRoomIsCalibrating(room) ||
                    checkRoomInNotifiedState(room)
            ) || false;

        // For Calibration, we load two more images, calibration icon in message + calibration icon in room tiles
        if (
            !imageGenerated &&
            ((isAnyRoomCalibrating && loadedImages === 7) ||
                loadedImages === 5)
        ) {
            generateCustomerAppScreenshot && generateCustomerAppScreenshot();
            setImageGenerated(true);
        }

        return (
            <div id="customer-portal-container">
                <div className={classes.page}>
                    <div className={classes.header}>
                        <div className={classes.menuButtonWrapper}>
                            <div className={classes.menuButtonUpper}></div>
                            <div className={classes.menuButtonLower}></div>
                        </div>
                        <div className={classes.logo}>
                            <img
                                onLoad={() => {
                                    setLoadedImages(
                                        (prevState) => prevState + 1
                                    );
                                }}
                                src="/assets/template/logo-tiko-img.png"
                                alt="Logo"
                            />
                        </div>
                        <div className={classes.iconsContainer}>
                            <img
                                onLoad={() => {
                                    setLoadedImages(
                                        (prevState) => prevState + 1
                                    );
                                }}
                                src="/assets/template/app-nav-icons.png"
                                alt="Nav Icons"
                            />
                        </div>
                    </div>
                    <div className={classes.houses}>{homeName}</div>
                    <div className={classes.content}>
                        <div className={classes.cardWrapper}>
                            <div className={classes.card}>
                                <div className={classes.roomsContainer}>
                                    {isAnyRoomCalibrating && (
                                        <div
                                            className={
                                                classes.calibrationMessage
                                            }
                                        >
                                            <span>
                                                La mesure de la température est
                                                en cours d'optimisation dans
                                                les pièces marquées d'un{' '}
                                                <img
                                                    onLoad={() => {
                                                        setLoadedImages(
                                                            (prevState) =>
                                                                prevState + 1
                                                        );
                                                    }}
                                                    src="/assets/template/calibration-icon-pink.png"
                                                    alt="Calibration Icon Message"
                                                />
                                                . Cliquez sur la pièce pour en
                                                savoir plus.
                                            </span>
                                        </div>
                                    )}
                                    <div className={classes.roomCardContainer}>
                                        {rooms &&
                                            rooms.map((room) => {
                                                const roomIsCalibrating =
                                                    checkRoomIsCalibrating(
                                                        room
                                                    ) ||
                                                    checkRoomInNotifiedState(
                                                        room
                                                    );
                                                const roomLastAverageTemp =
                                                    room.devices[0] &&
                                                    !roomIsCalibrating
                                                        ? parseFloat(
                                                              (
                                                                  room
                                                                      .devices[0]
                                                                      .lastAverageTemperature /
                                                                  100
                                                              ).toFixed(1)
                                                          ) + 'º'
                                                        : '-';

                                                const roomTargetTemp =
                                                    room.devices[0] &&
                                                    !roomIsCalibrating
                                                        ? parseFloat(
                                                              (
                                                                  room
                                                                      .devices[0]
                                                                      .lastTargetTemperature /
                                                                  100
                                                              ).toFixed(1)
                                                          ) + 'º'
                                                        : '-';

                                                return (
                                                    <div
                                                        key={room.roomId}
                                                        className={
                                                            classes.roomCard
                                                        }
                                                    >
                                                        <div
                                                            className={
                                                                classes.cardContent
                                                            }
                                                        >
                                                            <div
                                                                className={
                                                                    classes.roomName
                                                                }
                                                            >
                                                                {room.name}
                                                            </div>
                                                            <div
                                                                className={
                                                                    classes.TempContainer
                                                                }
                                                            >
                                                                <div
                                                                    className={
                                                                        classes.roomCardLeft
                                                                    }
                                                                >
                                                                    <div
                                                                        className={
                                                                            classes.readTemp
                                                                        }
                                                                    >
                                                                        {
                                                                            roomLastAverageTemp
                                                                        }
                                                                    </div>
                                                                    <div>
                                                                        {
                                                                            roomTargetTemp
                                                                        }
                                                                    </div>
                                                                </div>
                                                                {roomIsCalibrating && (
                                                                    <div
                                                                        className={
                                                                            classes.roomCardRight
                                                                        }
                                                                    >
                                                                        <img
                                                                            onLoad={() => {
                                                                                setLoadedImages(
                                                                                    (
                                                                                        prevState
                                                                                    ) =>
                                                                                        prevState +
                                                                                        1
                                                                                );
                                                                            }}
                                                                            src="/assets/template/calibration-icon-white.png"
                                                                            alt="Calibration Icon"
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div
                            className={`${classes.card} ${classes.cardPadding}`}
                        >
                            <div className={classes.cardTitle}>Modes</div>
                            <div className={classes.cardImage}>
                                <img
                                    onLoad={() => {
                                        setLoadedImages(
                                            (prevState) => prevState + 1
                                        );
                                    }}
                                    src="/assets/template/modes.png"
                                    alt="Modes img"
                                />
                            </div>
                        </div>
                        <div
                            className={`${classes.card} ${classes.cardPadding}`}
                        >
                            <div
                                className={`${classes.cardTitle} ${classes.consumationTitle}`}
                            >
                                Consommation de chauffage
                            </div>
                            <div className={classes.consumptionGraphsIcon}>
                                <img
                                    onLoad={() => {
                                        setLoadedImages(
                                            (prevState) => prevState + 1
                                        );
                                    }}
                                    src="/assets/template/analytics-img.png"
                                    alt="Analytics"
                                />
                            </div>

                            <div className={classes.cardImage}>
                                <img
                                    src="/assets/template/consumption.png"
                                    onLoad={() => {
                                        setLoadedImages(
                                            (prevState) => prevState + 1
                                        );
                                    }}
                                    alt="Consumption img"
                                />
                            </div>

                            {rooms &&
                                rooms.map((room) => (
                                    <div
                                        key={room.roomId}
                                        className={
                                            classes.roomConsumptionContainer
                                        }
                                    >
                                        <span
                                            className={
                                                classes.roomConsumptionTitle
                                            }
                                        >
                                            {room.name}
                                        </span>{' '}
                                        / 0 kWh
                                        <div
                                            className={
                                                classes.roomConsumptionBar
                                            }
                                        ></div>
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
);

export default CustomerAppTemplate;
